import { useUserData } from "@/shared/hooks/useUserData.hook";
import type { FunctionComponent } from "react";
import ImagenUsuario from "../../ImagenUsuario/ImagenUsuario";
import { RiMailLine, RiPhoneLine, RiIdCardLine, RiMenLine, RiWomenLine, RiPencilLine, RiPassValidLine } from "@remixicon/react";
import { useFormatCedula } from "@/shared/hooks/useFormatCedula";
import type { PerfilEntidad } from "@/shared/types/interfaces/user.interface";
import { storage } from "@/shared/utils/Const.utils";
import { StorageKeys } from "@/shared/types/enums/Storage.enum";
import { RolesMap } from "@/shared/types/maps";
import { useTranslation } from "react-i18next";
import { IconButton, Tooltip } from "@mui/material";
import { formatTelefono } from "@/shared/hooks/useFormatTelefono";
import { useAuthHook } from "@/shared/hooks/useAuth.hook";

interface PersonalConfigProps {
    openForm: () => void
    telefono: string
    correo: string
}

const PersonalConfig: FunctionComponent<PersonalConfigProps> = ({
    openForm,
    telefono,
    correo
}) => {

    const { t } = useTranslation();
    const {userData} = useUserData();
    let { formatCedula } = useFormatCedula();
    const perfil: PerfilEntidad | null = storage.get(StorageKeys.PERFIL);
    const { user } = useAuthHook();
    const activePerfil = user?.permisos?.find(p => p.Activo);
    let role =  perfil?.infoEntidad?.relacion?.idRol ? RolesMap.get(perfil?.infoEntidad?.relacion?.idRol) || t('menu.noProfile') : activePerfil?.Perfil || t('menu.noProfile');

    return (
        <>
        <div className="mx-auto pb-6 space-y-6">
            <div className="flex flex-col items-center">
                <ImagenUsuario
                    fullName={userData?.fullName || "No disponible"}
                    image={userData?.image || ""}
                    validado={userData?.validado || false}
                />
                <h2 className="mt-3 text-xl font-semibold text-gray-800 text-center">
                    {userData?.fullName.toUpperCase() || "No disponible"}
                </h2>
                <p className="text-sm text-gray-500">
                    {role.toUpperCase()}
                </p>
            </div>

            <div className="relative bg-white rounded-2xl p-6 space-y-4 border border-gray-200">
                <Tooltip title={t('configuracionPerfil.editInformation')} arrow>
                    <IconButton 
                        aria-label="Editar información" 
                        color="primary" 
                        className="absolute! -top-3! -right-3! p-2 bg-blue-50! border! border-blue-500!"
                        onClick={openForm}
                    >
                        <RiPencilLine className="primary" />
                    </IconButton>
                </Tooltip>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <InfoItem
                        icon={<RiIdCardLine className="w-5 h-5 text-blue-500" />}
                        label={t('configuracionPerfil.identification')}
                        value={userData?.identificacion ? formatCedula(userData.identificacion) : "No disponible"}
                    />
                    <InfoItem
                        icon={
                            userData?.genero === "Masculino"
                            ? <RiMenLine className="w-5 h-5 text-blue-500" />
                            : <RiWomenLine className="w-5 h-5 text-blue-500" />
                        }
                        label={t('configuracionPerfil.gender')}
                        value={userData?.genero || "No disponible"}
                    />
                    <InfoItem
                        icon={<RiMailLine className="w-5 h-5 text-blue-500" />}
                        label={t('configuracionPerfil.email')}
                        value={correo || "No disponible"}
                    />
                    <InfoItem
                        icon={<RiPhoneLine className="w-5 h-5 text-blue-500" />}
                        label={t('configuracionPerfil.phone')}
                        value={formatTelefono(telefono) || "No disponible"}
                    />
                    {
                        userData?.matricula
                        && (
                            <InfoItem
                                icon={<RiPassValidLine className="w-5 h-5 text-blue-500" />}
                                label={t('configuracionPerfil.license')}
                                value={userData.matricula || "No disponible"}
                            />
                        )
                    }
                </div>
            </div>
        </div>
        </>
    );
}

interface infoItemProps{
    icon: React.ReactNode;
    label: string;
    value: string;
}

const InfoItem = ({
    icon, label, value,
}: infoItemProps) => (
    <div className="flex items-start space-x-3 bg-gray-50 rounded-xl p-3 hover:bg-blue-50 transition-colors duration-200">
        <div className="flex-shrink-0 p-2 bg-blue-100 rounded-full">
            {icon}
        </div>
        <div>
            <p className="text-xs uppercase text-gray-500 font-medium">
                {label}
            </p>
            <p className="text-sm font-semibold text-gray-800">
                {value}
            </p>
        </div>
    </div>
);

export default PersonalConfig;